// --- Dependencies -------------------------------------------------------------------------------
import { useEffect } from "react";
import { Navigate, Outlet } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import useAuth from "@/hooks/useAuth";
import useFetch from "@/hooks/useFetch";
import type { TyAdmin } from "@/types";
import AppBar from "@/UI/components/AppBar";
import BottomBar from "@/UI/components/BottomBar";
import SideBar from "@/UI/components/SideBar";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
/**
 * The gate, and it is one gate rather than two: an admin has no organization to pick after signing
 * in, so a token is the whole of it.
 *
 * The sidebar is the first child of a plain row, so it lands on the inline-start edge — the right in
 * Arabic, the left in English — without the layout knowing which language is on.
 */
export default function DashboardLayout() {
    const token = useAuth((state) => state.token);
    const setAdmin = useAuth((state) => state.setAdmin);

    /**
     * The account, re-read once per mount rather than trusted from storage.
     *
     * `isSuper` and `isActive` decide what this app will even draw, and the persisted copy is as old
     * as the last sign-in — a revoked super admin would keep seeing the admins tab until they
     * happened to sign out. A 401 here is also what clears a token that was revoked server-side,
     * because `useFetch` signs out on one.
     */
    const session = useFetch<{ admin: TyAdmin }>("/auth/me", {}, !!token);

    useEffect(() => {
        if (session.data?.admin) setAdmin(session.data.admin);
    }, [session.data, setAdmin]);

    if (!token) return <Navigate to="/login" replace />;

    return (
        <div className="dashboard-layout">
            <SideBar />
            <div className="dashboard-layout__content">
                <AppBar />
                <main className="dashboard-layout__main">
                    <Outlet />
                </main>
            </div>
            <BottomBar />
        </div>
    );
}
